import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    {
      title: "Revenue Forecasting",
      description: "Predict future revenue based on historical data, seasonal trends, and patient behavior patterns",
      icon: "📈",
      benefits: [
        "95% accuracy in revenue predictions",
        "Identify growth opportunities",
        "Optimize resource allocation"
      ]
    },
    {
      title: "Patient Behavior Analysis",
      description: "Understand patient patterns and preferences to improve engagement and retention",
      icon: "👥",
      benefits: [
        "Predict no-show likelihood",
        "Identify at-risk patients",
        "Personalize patient communication"
      ]
    },
    {
      title: "Treatment Success Prediction",
      description: "AI-powered analysis of treatment outcomes and success rates",
      icon: "🎯",
      benefits: [
        "Predict treatment success rates",
        "Optimize treatment plans",
        "Improve patient outcomes"
      ]
    },
    {
      title: "Resource Optimization",
      description: "Smart allocation of staff, equipment, and resources based on predicted demand",
      icon: "⚡",
      benefits: [
        "Reduce operational costs",
        "Maximize staff efficiency",
        "Optimize inventory management"
      ]
    }
  ];

  const caseStudies = [
    {
      title: "Dr. Sarah's Practice Growth",
      description: "How predictive analytics helped increase revenue by 35% in 6 months",
      metrics: [
        { label: "Revenue Growth", value: "35%" },
        { label: "Patient Retention", value: "92%" },
        { label: "Resource Efficiency", value: "40%" }
      ]
    },
    {
      title: "City Dental Group",
      description: "Transforming operations with AI-powered insights",
      metrics: [
        { label: "Cost Reduction", value: "25%" },
        { label: "Patient Satisfaction", value: "98%" },
        { label: "Staff Productivity", value: "45%" }
      ]
    }
  ];

  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Predictive Analytics
            </h1>
            <p className="text-xl text-gray-600">
              Transform your dental practice with data-driven insights and intelligent predictions
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Case Studies */}
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] mb-8 text-center">
            Success Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                  {study.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {study.description}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {study.metrics.map((metric, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-2xl font-bold text-orange-500">
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-600">
                        {metric.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl mb-8">
            Start leveraging the power of AI-driven analytics today
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/schedule-demo"
              className="bg-white text-orange-500 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule a Demo
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-500 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 